// ConsequenceWatcher — when a delayed decision consequence lands (weeks after the
// choice), this shows the recap: what happened, the framework it taught, and the
// one-line lesson. Reads the persisted landedConsequences queue, so it survives a
// reload and shows them one at a time. Mounted globally.

import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useRef } from 'react';
import { useGameState, useDispatch } from '@biz-kid/store';
import { Card, Eyebrow, Button, money } from '../../ui/kit.js';
import { FRAMEWORKS, type Framework } from './weeklyDecisionDeck.js';
import { useModalSlot } from '../../ui/modalQueue.js';
import { useVoice } from '../../voice/VoiceContext.js';

export function ConsequenceWatcher() {
  const landed = useGameState((s) => s.landedConsequences);
  const dispatch = useDispatch();

  const current = landed[0];
  // Priority 70 — queue behind year/vision/payday/resilience.
  const { canShow } = useModalSlot('consequence', !!current, 70);

  // MIKO SPEAKS — a past choice coming due is a story beat AND the lesson
  // moment; read it aloud once per consequence (keyed by id).
  const { speak } = useVoice();
  const spokeIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!current || !canShow || spokeIdRef.current === current.id) return;
    spokeIdRef.current = current.id;
    const opener = current.cash >= 0 ? 'Your choice paid off!' : 'Remember that choice you made? It just came back around.';
    void speak(`${opener} ${current.lesson}`);
  }, [current, canShow, speak]);

  if (!current || !canShow) return null;

  const fw = FRAMEWORKS[current.framework as Framework] ?? { label: current.framework, icon: '💡', color: 'var(--brand)' };
  const good = current.cash >= 0;

  return (
    <AnimatePresence>
      <motion.div
        key={current.id}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={{ position: 'fixed', inset: 0, zIndex: 76, display: 'grid', placeItems: 'center', padding: 18, background: 'rgba(10,14,26,0.6)', backdropFilter: 'blur(7px)', WebkitBackdropFilter: 'blur(7px)' }}
      >
        <motion.div initial={{ y: 16, scale: 0.97 }} animate={{ y: 0, scale: 1 }} style={{ width: '100%', maxWidth: 420 }}>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 40 }}>{good ? '🌱' : '🍂'}</div>
              <Eyebrow color={fw.color}>A past choice comes due · {fw.icon} {fw.label}</Eyebrow>
              {current.cash !== 0 && (
                <div className="bk-tnum" style={{ fontSize: 30, fontWeight: 900, color: good ? 'var(--up)' : 'var(--down)', marginTop: 4 }}>
                  {good ? '+' : '−'}{money(Math.abs(current.cash))}
                </div>
              )}
            </div>

            <div style={{ marginTop: 12, padding: 14, borderRadius: 14, background: 'var(--surface-2)', border: `1px solid ${fw.color}` }}>
              <div style={{ fontSize: 10.5, fontWeight: 800, color: fw.color, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>
                {fw.label} · the lesson
              </div>
              <div style={{ fontSize: 14.5, color: 'var(--text)', lineHeight: 1.55 }}>{current.lesson}</div>
            </div>

            <div style={{ marginTop: 16 }}>
              <Button tone="primary" size="lg" full onClick={() => dispatch({ kind: 'AckConsequence', id: current.id })}>Got it →</Button>
            </div>
          </Card>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
